import React, { useState, useEffect } from 'react';
import { Store, ChevronRight, Palette, Package2, CreditCard, ShoppingCart, Plus, X, ChevronDown, Search, Code2, Copy, MapPin, Wallet, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from '../components/toasts/SimpleToast';
import CreateTerritoriesModal from '../components/CreateTerritoriesModal';
import { ShippingMethod, ShippingProductSurcharge } from '../lib/storefront';

export default function OnlineStore() {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();

  // Store design settings state
  const [storeDesign, setStoreDesign] = useState({
    sidebarColor: '#374151', // Default gray-700
    sidebarTextColor: '#ffffff', // Default white
    headerColor: '#ffffff',  // Default white
    backgroundColor: '#f9fafb', // Default gray-50
    accentColor: '#3b82f6',   // Default blue-500
    businessNameColor: '#111827', // Default gray-900
    browseProductsColor: '#6b7280', // Default gray-500
    showBusinessLogo: true,   // Default show logo
    showBusinessName: true,   // Default show name
    showPopularProducts: false, // Default hide popular products
    popularProductIds: [] as string[],     // Default empty array
    displayStockLevels: false,   // Default hide stock levels
    hideOutOfStockProducts: false // Default show all products
  });

  // Shipping settings state
  const [shippingSettings, setShippingSettings] = useState({
    enableInStorePickup: true,
    pickupAddress: '',
    shippingMethods: [] as ShippingMethod[],
    enableOrderNotes: true
  });

  // Store availability + checkout behavior
  const [isStoreEnabled, setIsStoreEnabled] = useState(false);
  const [isTogglingStore, setIsTogglingStore] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState<'invoice_on_delivery' | 'pay_at_checkout'>('invoice_on_delivery');
  const [embedOrigins, setEmbedOrigins] = useState('');

  // Pay-at-checkout needs a connected Stripe account — same gate the
  // store-checkout function uses before creating a payment link.
  const isStripeReady = !!(
    (selectedBusiness as any)?.stripe_connect_account_id &&
    (selectedBusiness as any)?.online_payments_enabled
  );

  // Customer wallet (portal): visibility, self-serve top-ups, bonus tiers
  const [walletEnabled, setWalletEnabled] = useState(false);
  const [walletTopupEnabled, setWalletTopupEnabled] = useState(false);
  // Hosted shop (portal Shop button + /store page). Off = embeds only.
  const [portalShopEnabled, setPortalShopEnabled] = useState(true);
  // Online signups create an Open CRM lead; a later order auto-wins it.
  const [createLeadOnSignup, setCreateLeadOnSignup] = useState(false);
  // Where signup leads land — null means first pipeline / first stage.
  const [signupLeadPipelineId, setSignupLeadPipelineId] = useState<number | null>(null);
  const [signupLeadStageId, setSignupLeadStageId] = useState<number | null>(null);
  const [crmPipelines, setCrmPipelines] = useState<Array<{ id: number; pipeline_name: string; stages: Array<{ id: number; name: string }> }>>([]);
  const [walletPromos, setWalletPromos] = useState<Array<{ id?: string; minAmount: string; bonus: string }>>([]);
  const [removedPromoIds, setRemovedPromoIds] = useState<string[]>([]);
  const [isSavingWallet, setIsSavingWallet] = useState(false);
  const [isWalletExpanded, setIsWalletExpanded] = useState(false);

  // Order notification emails (Shopify-style, per-event toggles)
  const [orderNotifs, setOrderNotifs] = useState({
    order_placed: true,
    order_confirmed: false,
    out_for_delivery: false,
    delivered: false,
    invoiced: false,
    cancelled: true,
    staff_new_order: false,
  });
  const [staffAlertEmails, setStaffAlertEmails] = useState('');
  const [isSavingNotifs, setIsSavingNotifs] = useState(false);
  const [isNotifsExpanded, setIsNotifsExpanded] = useState(false);

  // Delivery area validation
  const [enforceDeliveryAreas, setEnforceDeliveryAreas] = useState(false);
  const [deliveryAreaIds, setDeliveryAreaIds] = useState<string[]>([]);
  const [serviceAreas, setServiceAreas] = useState<Array<{ id: string; name: string; postal_codes: string[] }>>([]);
  const [isAreasModalOpen, setIsAreasModalOpen] = useState(false);

  // UI state
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isSavingShipping, setIsSavingShipping] = useState(false);
  // Settings loaded cleanly — saves are blocked until true (see ensureSettingsLoaded).
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isDesignExpanded, setIsDesignExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  const [isEmbedExpanded, setIsEmbedExpanded] = useState(false);

  // Embed snippet generator state
  const [embedType, setEmbedType] = useState<'grid' | 'product' | 'cart-button' | 'account-button'>('grid');
  const [embedProductId, setEmbedProductId] = useState('');

  // Popular products state
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Load store design and shipping settings
  const loadStoreSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // CRM pipelines — options for the signup-lead pipeline/stage pickers.
      supabase
        .from('pipelines')
        .select('id, pipeline_name, stages')
        .eq('business_id', selectedBusiness.id)
        .order('sort_order', { ascending: true })
        .order('pipeline_name', { ascending: true })
        .then(({ data: pipelineRows }) => {
          setCrmPipelines((pipelineRows || []).map((p: any) => ({
            id: p.id,
            pipeline_name: p.pipeline_name,
            stages: Array.isArray(p.stages) ? p.stages : [],
          })));
        });

      const { data, error } = await supabase
        .from('online_store_settings')
        .select('sidebar_color, sidebar_text_color, header_color, background_color, accent_color, business_name_color, browse_products_color, show_business_logo, show_business_name, show_popular_products, popular_product_ids, display_stock_levels, hide_out_of_stock_products, enable_in_store_pickup, pickup_address, shipping_methods, is_enabled, checkout_mode, embed_allowed_origins, enforce_delivery_areas, delivery_area_ids, wallet_enabled, wallet_topup_enabled, order_notifications, enable_order_notes, create_crm_lead_on_signup, signup_lead_pipeline_id, signup_lead_stage_id, portal_shop_enabled')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No settings found — defaults are the real state, saving is safe.
          setSettingsLoaded(true);
          return;
        }
        throw error;
      }

      if (data) {
        setStoreDesign({
          sidebarColor: data.sidebar_color || '#374151',
          sidebarTextColor: data.sidebar_text_color || '#ffffff',
          headerColor: data.header_color || '#ffffff',
          backgroundColor: data.background_color || '#f9fafb',
          accentColor: data.accent_color || '#3b82f6',
          businessNameColor: data.business_name_color || '#111827',
          browseProductsColor: data.browse_products_color || '#6b7280',
          showBusinessLogo: data.show_business_logo ?? true,
          showBusinessName: data.show_business_name ?? true,
          showPopularProducts: data.show_popular_products ?? false,
          popularProductIds: data.popular_product_ids || [],
          displayStockLevels: data.display_stock_levels ?? false,
          hideOutOfStockProducts: data.hide_out_of_stock_products ?? false
        });
        
        // Load shipping settings
        setShippingSettings({
          enableInStorePickup: data.enable_in_store_pickup ?? true,
          pickupAddress: data.pickup_address || '',
          shippingMethods: data.shipping_methods || [],
          enableOrderNotes: data.enable_order_notes ?? true
        });

        setIsStoreEnabled(data.is_enabled === true);
        // A saved pay_at_checkout with Stripe since disconnected shows as
        // invoice-on-delivery — matching what checkout actually does.
        setCheckoutMode(data.checkout_mode === 'pay_at_checkout' && isStripeReady ? 'pay_at_checkout' : 'invoice_on_delivery');
        setEmbedOrigins((data.embed_allowed_origins || []).join(', '));
        setEnforceDeliveryAreas(data.enforce_delivery_areas === true);
        setDeliveryAreaIds(data.delivery_area_ids || []);
        setWalletEnabled(data.wallet_enabled === true);
        setWalletTopupEnabled(data.wallet_topup_enabled === true);
        setCreateLeadOnSignup(data.create_crm_lead_on_signup === true);
        setSignupLeadPipelineId(data.signup_lead_pipeline_id ?? null);
        setSignupLeadStageId(data.signup_lead_stage_id ?? null);
        setPortalShopEnabled(data.portal_shop_enabled !== false);

        const notifs = data.order_notifications || {};
        setOrderNotifs({
          order_placed: notifs.order_placed !== false,
          order_confirmed: notifs.order_confirmed === true,
          out_for_delivery: notifs.out_for_delivery === true,
          delivered: notifs.delivered === true,
          invoiced: notifs.invoiced === true,
          cancelled: notifs.cancelled !== false,
          staff_new_order: notifs.staff_new_order === true,
        });
        setStaffAlertEmails((notifs.staff_emails || []).join(', '));
      }

      // Wallet bonus tiers (separate table)
      const { data: promoRows } = await supabase
        .from('wallet_topup_promotions')
        .select('id, min_amount_cents, bonus_cents, is_active')
        .eq('business_id', selectedBusiness.id)
        .eq('is_active', true)
        .order('min_amount_cents', { ascending: true });
      setWalletPromos(
        (promoRows || []).map(p => ({
          id: p.id,
          minAmount: (p.min_amount_cents / 100).toFixed(2),
          bonus: (p.bonus_cents / 100).toFixed(2),
        }))
      );
      setRemovedPromoIds([]);
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error loading store settings:', error);
      // Saving with default state would overwrite the real settings (it wiped
      // a business's shipping methods once) — block saves until a clean load.
      setSettingsLoaded(false);
      toast.error({
        title: "Failed to load store settings",
        description: "Refresh the page before saving — saving now could overwrite your configuration.",
        duration: 6000,
      });
    }
  };

  /** Saves write whole sections from state — never allow one after a failed load. */
  const ensureSettingsLoaded = (): boolean => {
    if (settingsLoaded) return true;
    toast.error({
      title: "Settings didn't load",
      description: "Refresh the page before saving — saving now would overwrite your store configuration with defaults.",
      duration: 6000,
    });
    return false;
  };

  // Save store design settings
  const saveStoreDesignSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    if (!ensureSettingsLoaded()) return;

    try {
      setIsSavingDesign(true);
      console.log('Saving design settings for business:', selectedBusiness.id);
      console.log('Design data:', storeDesign);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      console.log('Existing data check:', { existingData, selectError });

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      const settingsData = {
        business_id: selectedBusiness.id,
        sidebar_color: storeDesign.sidebarColor,
        sidebar_text_color: storeDesign.sidebarTextColor,
        header_color: storeDesign.headerColor,
        background_color: storeDesign.backgroundColor,
        accent_color: storeDesign.accentColor,
        business_name_color: storeDesign.businessNameColor,
        browse_products_color: storeDesign.browseProductsColor,
        show_business_logo: storeDesign.showBusinessLogo,
        show_business_name: storeDesign.showBusinessName,
        show_popular_products: storeDesign.showPopularProducts,
        popular_product_ids: storeDesign.popularProductIds,
        display_stock_levels: storeDesign.displayStockLevels,
        hide_out_of_stock_products: storeDesign.hideOutOfStockProducts
      };

      console.log('Settings data to save:', settingsData);

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(settingsData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        // Create new settings record
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(settingsData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Design settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving store design settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save design settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingDesign(false);
    }
  };

  // Turn the public store (and embeds) on/off
  const toggleStoreEnabled = async () => {
    if (!selectedBusiness?.id || isTogglingStore) return;
    setIsTogglingStore(true);
    const next = !isStoreEnabled;
    try {
      const { error } = await supabase
        .from('online_store_settings')
        .upsert({ business_id: selectedBusiness.id, is_enabled: next }, { onConflict: 'business_id' });
      if (error) throw error;
      setIsStoreEnabled(next);
      toast.success({
        title: next ? 'Store enabled' : 'Store disabled',
        description: next
          ? 'Customers can now browse and order from your online store.'
          : 'Your store page and embeds are now hidden from customers.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error toggling store:', error);
      toast.error({ title: 'Error', description: 'Failed to update store status', duration: 3000 });
    } finally {
      setIsTogglingStore(false);
    }
  };

  // Load the business's service areas for the delivery-area picker
  const loadServiceAreas = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data, error } = await supabase
        .from('service_areas')
        .select('id, name, postal_codes')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setServiceAreas(data || []);
    } catch (error) {
      console.error('Error loading service areas:', error);
    }
  };

  const toggleDeliveryArea = (areaId: string) => {
    setDeliveryAreaIds(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  // Save shipping settings
  const saveShippingSettings = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      toast.error({
        title: "Error",
        description: "No business selected",
        duration: 3000,
      });
      return;
    }

    const unrated = shippingSettings.shippingMethods.find(
      m => m.pricing === 'by_area' && (m.areaRates || []).filter(r => serviceAreas.some(a => a.id === r.areaId)).length === 0
    );
    if (unrated) {
      toast.error({
        title: 'Missing area prices',
        description: `"${unrated.name || 'Unnamed method'}" is priced by area but has no areas selected — customers couldn't use it anywhere.`,
        duration: 5000,
      });
      return;
    }

    if (!ensureSettingsLoaded()) return;

    try {
      setIsSavingShipping(true);
      console.log('Saving shipping settings for business:', selectedBusiness.id);
      console.log('Shipping data:', shippingSettings);

      // First, try to update existing settings
      const { data: existingData, error: selectError } = await supabase
        .from('online_store_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        console.error('Error checking existing settings:', selectError);
        throw selectError;
      }

      // Sanitize methods: drop surcharge rules with no product picked and
      // area rates for areas that no longer exist.
      const cleanedMethods = shippingSettings.shippingMethods.map(m => ({
        ...m,
        areaRates: (m.areaRates || []).filter(r => serviceAreas.some(a => a.id === r.areaId)),
        productSurcharges: (m.productSurcharges || []).filter(r => r.stockItemId),
      }));

      const shippingData = {
        business_id: selectedBusiness.id,
        enable_in_store_pickup: shippingSettings.enableInStorePickup,
        pickup_address: shippingSettings.pickupAddress || null,
        shipping_methods: cleanedMethods,
        enable_order_notes: shippingSettings.enableOrderNotes,
        create_crm_lead_on_signup: createLeadOnSignup,
        signup_lead_pipeline_id: signupLeadPipelineId,
        signup_lead_stage_id: signupLeadStageId,
        portal_shop_enabled: portalShopEnabled,
        checkout_mode: checkoutMode,
        embed_allowed_origins: embedOrigins.trim()
          ? embedOrigins.split(',').map(o => o.trim().replace(/\/$/, '')).filter(Boolean)
          : null,
        enforce_delivery_areas: enforceDeliveryAreas,
        // Drop selections for areas that no longer exist.
        delivery_area_ids: deliveryAreaIds.filter(id => serviceAreas.some(a => a.id === id))
      };

      if (existingData) {
        console.log('Updating existing settings...');
        const { error: updateError } = await supabase
          .from('online_store_settings')
          .update(shippingData)
          .eq('business_id', selectedBusiness.id);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
        console.log('Shipping settings updated successfully');
      } else {
        console.log('Creating new settings record...');
        const { error: insertError } = await supabase
          .from('online_store_settings')
          .insert(shippingData);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        console.log('Shipping settings created successfully');
      }

      toast.success({
        title: "Success",
        description: "Shipping settings saved successfully!",
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      toast.error({
        title: "Error",
        description: `Failed to save shipping settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingShipping(false);
    }
  };

  // Save wallet settings + bonus tiers
  const saveNotificationSettings = async () => {
    if (!selectedBusiness?.id) return;

    const emails = staffAlertEmails
      .split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(Boolean);
    const invalid = emails.filter(e => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
    if (orderNotifs.staff_new_order && emails.length === 0) {
      toast.error({
        title: 'Add a staff email',
        description: 'Enter at least one email address to receive new order alerts.',
        duration: 4000,
      });
      return;
    }
    if (invalid.length > 0) {
      toast.error({
        title: 'Check the staff emails',
        description: `Not a valid email: ${invalid.join(', ')}`,
        duration: 4000,
      });
      return;
    }

    if (!ensureSettingsLoaded()) return;

    try {
      setIsSavingNotifs(true);
      const { error } = await supabase
        .from('online_store_settings')
        .upsert(
          {
            business_id: selectedBusiness.id,
            order_notifications: { ...orderNotifs, staff_emails: emails },
          },
          { onConflict: 'business_id' }
        );
      if (error) throw error;
      toast.success({
        title: 'Success',
        description: 'Notification settings saved successfully!',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error({
        title: 'Error',
        description: `Failed to save notification settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingNotifs(false);
    }
  };

  const saveWalletSettings = async () => {
    if (!selectedBusiness?.id) return;

    // Validate tiers before writing anything.
    const parsedPromos: Array<{ id?: string; min_amount_cents: number; bonus_cents: number }> = [];
    for (const p of walletPromos) {
      const minCents = Math.round(parseFloat(p.minAmount) * 100);
      const bonusCents = Math.round(parseFloat(p.bonus) * 100);
      if (!Number.isFinite(minCents) || minCents <= 0 || !Number.isFinite(bonusCents) || bonusCents <= 0) {
        toast.error({
          title: 'Check your bonus tiers',
          description: 'Every tier needs a top-up amount and a bonus greater than $0.',
          duration: 4000,
        });
        return;
      }
      parsedPromos.push({ id: p.id, min_amount_cents: minCents, bonus_cents: bonusCents });
    }

    if (!ensureSettingsLoaded()) return;

    try {
      setIsSavingWallet(true);

      const { error: settingsError } = await supabase
        .from('online_store_settings')
        .upsert(
          {
            business_id: selectedBusiness.id,
            wallet_enabled: walletEnabled,
            wallet_topup_enabled: walletTopupEnabled,
          },
          { onConflict: 'business_id' }
        );
      if (settingsError) throw settingsError;

      // Removed tiers are deactivated (not deleted) so past top-ups keep
      // their promotion reference.
      if (removedPromoIds.length > 0) {
        const { error } = await supabase
          .from('wallet_topup_promotions')
          .update({ is_active: false })
          .in('id', removedPromoIds);
        if (error) throw error;
      }
      for (const promo of parsedPromos) {
        if (promo.id) {
          const { error } = await supabase
            .from('wallet_topup_promotions')
            .update({ min_amount_cents: promo.min_amount_cents, bonus_cents: promo.bonus_cents })
            .eq('id', promo.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('wallet_topup_promotions')
            .insert({
              business_id: selectedBusiness.id,
              min_amount_cents: promo.min_amount_cents,
              bonus_cents: promo.bonus_cents,
            });
          if (error) throw error;
        }
      }

      setRemovedPromoIds([]);
      await loadStoreSettings();
      toast.success({
        title: 'Success',
        description: 'Wallet settings saved successfully!',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving wallet settings:', error);
      toast.error({
        title: 'Error',
        description: `Failed to save wallet settings: ${(error as any)?.message || 'Unknown error'}`,
        duration: 5000,
      });
    } finally {
      setIsSavingWallet(false);
    }
  };

  // Shipping methods management
  const addShippingMethod = () => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: [...prev.shippingMethods, { name: '', price: 0, pricing: 'flat' }]
    }));
  };

  const patchShippingMethod = (index: number, patch: Partial<ShippingMethod>) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) =>
        i === index ? { ...method, ...patch } : method
      )
    }));
  };

  const removeShippingMethod = (index: number) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.filter((_, i) => i !== index)
    }));
  };

  /** Set/replace the price for an area on a method; null removes the rate. */
  const setMethodAreaRate = (index: number, areaId: string, price: number | null) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) => {
        if (i !== index) return method;
        const rates = (method.areaRates || []).filter(r => r.areaId !== areaId);
        if (price !== null) rates.push({ areaId, price });
        return { ...method, areaRates: rates };
      })
    }));
  };

  const patchMethodSurcharge = (
    methodIndex: number,
    ruleIndex: number,
    patch: Partial<ShippingProductSurcharge> | null
  ) => {
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) => {
        if (i !== methodIndex) return method;
        const rules = [...(method.productSurcharges || [])];
        if (patch === null) rules.splice(ruleIndex, 1);
        else rules[ruleIndex] = { ...rules[ruleIndex], ...patch };
        return { ...method, productSurcharges: rules };
      })
    }));
  };

  const addMethodSurcharge = (methodIndex: number) => {
    if (availableProducts.length === 0) loadAvailableProducts();
    setShippingSettings(prev => ({
      ...prev,
      shippingMethods: prev.shippingMethods.map((method, i) =>
        i === methodIndex
          ? {
              ...method,
              productSurcharges: [
                ...(method.productSurcharges || []),
                { stockItemId: '', productName: '', baseUnits: 0, baseFee: 0, extraPerUnit: 0 },
              ],
            }
          : method
      )
    }));
  };

  // Load available products for popular products selection
  const loadAvailableProducts = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setIsLoadingProducts(true);
      const { data, error } = await supabase
        .from('stock_items')
        .select('id, name, description, price, quantity, is_one_time, is_deleted')
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false)
        .order('name');

      if (error) throw error;
      // Filter out one-time items with 0 quantity
      const filteredData = (data || []).filter(item => {
        if (item.is_one_time && item.quantity === 0) return false;
        return true;
      });
      setAvailableProducts(filteredData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Toggle product selection for popular products
  const toggleProductSelection = (productId: string) => {
    setStoreDesign(prev => {
      const currentIds = prev.popularProductIds || [];
      if (currentIds.includes(productId)) {
        // Remove product
        return {
          ...prev,
          popularProductIds: currentIds.filter(id => id !== productId)
        };
      } else if (currentIds.length < 10) {
        // Add product (max 10)
        return {
          ...prev,
          popularProductIds: [...currentIds, productId]
        };
      }
      return prev;
    });
  };

  // Load settings on component mount
  useEffect(() => {
    loadStoreSettings();
    loadServiceAreas();
  }, [selectedBusiness?.id]);

  // Copyable code snippet box for the embed generator
  function SnippetBox({ code }: { code: string }) {
    return (
      <div className="relative">
        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 pr-10 overflow-x-auto whitespace-pre-wrap break-all">{code}</pre>
        <button
          onClick={() => {
            navigator.clipboard.writeText(code).then(() =>
              toast.success({ title: 'Copied', description: 'Snippet copied to clipboard', duration: 2000 })
            );
          }}
          className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200"
          title="Copy"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // Load products when popular products is enabled
  useEffect(() => {
    if (storeDesign.showPopularProducts && availableProducts.length === 0) {
      loadAvailableProducts();
    }
  }, [storeDesign.showPopularProducts]);

  // Load products when saved shipping methods carry product surcharge rules,
  // so the rule dropdowns can display their selections.
  useEffect(() => {
    const hasRules = shippingSettings.shippingMethods.some(m => (m.productSurcharges || []).length > 0);
    if (hasRules && availableProducts.length === 0 && !isLoadingProducts) {
      loadAvailableProducts();
    }
  }, [shippingSettings.shippingMethods]);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Online Store</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your customer-facing online store</p>
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              Configure Store
            </button>
          </div>

          {/* Store Status */}
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${isStoreEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Store className={`h-6 w-6 ${isStoreEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-900">Store Status</h2>
                  <p className="text-sm text-gray-600">
                    {isStoreEnabled
                      ? 'Your online store is live and accepting orders'
                      : 'Your store is off — customers cannot browse or order'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-medium ${isStoreEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {isStoreEnabled ? 'Active' : 'Off'}
                </span>
                <button
                  type="button"
                  onClick={toggleStoreEnabled}
                  disabled={isTogglingStore}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    isStoreEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  aria-pressed={isStoreEnabled}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      isStoreEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            
            {/* Store URL */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-white rounded-lg px-3 py-2 border border-gray-300">
                  <code className="text-sm text-blue-600">
                    {window.location.origin}/{selectedBusiness?.slug || 'your-business'}/store
                  </code>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => {
                      const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                      window.open(storeUrl, '_blank');
                    }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Preview
                  </button>
                  <button 
                    onClick={() => {
                      const storeUrl = `${window.location.origin}/${selectedBusiness?.slug}/store`;
                      navigator.clipboard.writeText(storeUrl).then(() => {
                        toast.success({
                          title: 'Success',
                          description: 'Store link copied to clipboard!',
                          duration: 3000
                        });
                      }).catch(() => {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = storeUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        toast.success({
                          title: 'Success',
                          description: 'Store link copied to clipboard!',
                          duration: 3000
                        });
                      });
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Store Configuration */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Store Configuration</h2>
              <p className="text-sm text-gray-600 mt-1">Configure your online store settings and appearance</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Store Design Section - Will be expanded in next update */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsDesignExpanded(!isDesignExpanded)}
                      className="w-full flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Palette className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Store Design</h3>
                          <p className="text-sm text-gray-600">Customize colors, logo, and layout</p>
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isDesignExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  
                  {isDesignExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                      <p className="text-sm text-gray-500 text-center py-4">
                        Complete design settings will be added in the next update...
                      </p>
                      
                      {/* Save Button */}
                      <div className="pt-2 border-t border-gray-200">
                        <button 
                          onClick={saveStoreDesignSettings}
                          disabled={isSavingDesign}
                          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingDesign ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Design Changes'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Catalog Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Package2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Product Catalog</h3>
                          <p className="text-sm text-gray-600">Manage categories and visibility</p>
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isCatalogExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                  
                  {isCatalogExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                      {/* Stock Display Settings */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Customer Display Settings</h4>
                        
                        {/* Display Stock Levels */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Display Stock Levels
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Show stock quantities to customers on the store
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={storeDesign.displayStockLevels}
                            onChange={(e) => setStoreDesign(prev => ({ ...prev, displayStockLevels: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>

                        {/* Hide Out of Stock Products */}
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Hide Out of Stock Products
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Don't show products with zero stock to customers
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={storeDesign.hideOutOfStockProducts}
                            onChange={(e) => setStoreDesign(prev => ({ ...prev, hideOutOfStockProducts: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      {/* Save Button */}
                      <div className="pt-2 border-t border-gray-200">
                        <button 
                          onClick={saveStoreDesignSettings}
                          disabled={isSavingDesign}
                          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingDesign ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Catalog Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Settings Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsShippingExpanded(!isShippingExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CreditCard className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Payment Settings</h3>
                          <p className="text-sm text-gray-600">Configure payment methods and shipping</p>
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isShippingExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isShippingExpanded && (
                    <div className="px-4 pb-4 space-y-6">
                      {/* Checkout mode */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Checkout Mode</h4>
                        <div className="space-y-2">
                          <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${checkoutMode === 'invoice_on_delivery' ? 'border-green-500 bg-green-50/40' : 'border-gray-200'}`}>
                            <input
                              type="radio"
                              checked={checkoutMode === 'invoice_on_delivery'}
                              onChange={() => setCheckoutMode('invoice_on_delivery')}
                              className="mt-0.5 accent-green-600"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-800">Invoice on delivery</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Orders enter your Orders pipeline; you generate the invoice and collect payment when the order is delivered or picked up.
                              </span>
                            </span>
                          </label>
                          <label className={`flex items-start gap-3 rounded-lg border p-3 ${
                            !isStripeReady
                              ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                              : checkoutMode === 'pay_at_checkout'
                                ? 'border-green-500 bg-green-50/40 cursor-pointer'
                                : 'border-gray-200 cursor-pointer'
                          }`}>
                            <input
                              type="radio"
                              checked={checkoutMode === 'pay_at_checkout'}
                              onChange={() => setCheckoutMode('pay_at_checkout')}
                              disabled={!isStripeReady}
                              className="mt-0.5 accent-green-600 disabled:cursor-not-allowed"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-800">Pay at checkout</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Customers pay online immediately; the invoice is issued and stock deducted at checkout.
                              </span>
                              {!isStripeReady && (
                                <span className="block text-xs font-medium text-amber-600 mt-1">
                                  Connect Stripe payments in your invoice settings to enable this option.
                                </span>
                              )}
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Shipping Options Section */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Shipping Options</h4>
                        
                        {/* In-Store Pickup */}
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="enablePickup"
                              checked={shippingSettings.enableInStorePickup}
                              onChange={(e) => setShippingSettings(prev => ({
                                ...prev,
                                enableInStorePickup: e.target.checked
                              }))}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="enablePickup" className="text-sm font-medium text-gray-700">
                              In-Store Pickup
                            </label>
                          </div>
                          
                          {shippingSettings.enableInStorePickup && (
                            <div className="ml-7 space-y-2">
                              <p className="text-xs text-gray-600">
                                Using business address: <span className="font-medium">{selectedBusiness?.address || 'No address set'}</span>
                              </p>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Custom pickup address (optional)
                                </label>
                                <input
                                  type="text"
                                  value={shippingSettings.pickupAddress}
                                  onChange={(e) => setShippingSettings(prev => ({
                                    ...prev,
                                    pickupAddress: e.target.value
                                  }))}
                                  placeholder="Enter custom pickup address..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                />
                              </div>
                            </div>
                          )}

                          {/* Delivery notes at checkout */}
                          <div className="flex items-start space-x-3 pt-1">
                            <input
                              type="checkbox"
                              id="enableOrderNotes"
                              checked={shippingSettings.enableOrderNotes}
                              onChange={(e) => setShippingSettings(prev => ({
                                ...prev,
                                enableOrderNotes: e.target.checked
                              }))}
                              className="h-4 w-4 mt-0.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="enableOrderNotes" className="text-sm text-gray-700">
                              <span className="font-medium">Delivery notes at checkout</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Show a "Delivery instructions" box so customers can leave notes like gate codes or where to drop off.
                              </span>
                            </label>
                          </div>

                          {/* Hosted shop visibility (embeds keep working when off) */}
                          <div className="flex items-start space-x-3 pt-1">
                            <input
                              type="checkbox"
                              id="portalShopEnabled"
                              checked={portalShopEnabled}
                              onChange={(e) => setPortalShopEnabled(e.target.checked)}
                              className="h-4 w-4 mt-0.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="portalShopEnabled" className="text-sm text-gray-700">
                              <span className="font-medium">Show the hosted Shop</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                Shows the Shop button in the customer portal and the hosted store page. Turn off if you
                                only embed store widgets on your own website — embeds and checkout keep working.
                              </span>
                            </label>
                          </div>

                          {/* CRM lead on online signup */}
                          <div className="flex items-start space-x-3 pt-1">
                            <input
                              type="checkbox"
                              id="createLeadOnSignup"
                              checked={createLeadOnSignup}
                              onChange={(e) => setCreateLeadOnSignup(e.target.checked)}
                              className="h-4 w-4 mt-0.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor="createLeadOnSignup" className="text-sm text-gray-700">
                              <span className="font-medium">Create a CRM lead for online signups</span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                New customer signups open a lead in your CRM (source "Online Signup"). When they place
                                an order, the lead is automatically marked Won with the order value — same as bookings.
                              </span>
                            </label>
                          </div>

                          {/* Which pipeline/stage signup leads land in */}
                          {createLeadOnSignup && (
                            <div className="ml-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Pipeline</label>
                                <select
                                  value={signupLeadPipelineId ?? ''}
                                  onChange={(e) => {
                                    const id = e.target.value ? Number(e.target.value) : null;
                                    setSignupLeadPipelineId(id);
                                    // Stage belongs to the pipeline — reset on change.
                                    setSignupLeadStageId(null);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                >
                                  <option value="">Default (first pipeline)</option>
                                  {crmPipelines.map(p => (
                                    <option key={p.id} value={p.id}>{p.pipeline_name}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Stage</label>
                                <select
                                  value={signupLeadStageId ?? ''}
                                  onChange={(e) => setSignupLeadStageId(e.target.value ? Number(e.target.value) : null)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                >
                                  <option value="">Default (first stage)</option>
                                  {(crmPipelines.find(p => p.id === signupLeadPipelineId)?.stages
                                    ?? crmPipelines[0]?.stages
                                    ?? []
                                  ).map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Shipping Methods */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Additional Shipping Methods</h4>
                          <button
                            onClick={addShippingMethod}
                            className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Add Method</span>
                          </button>
                        </div>
                        
                        {shippingSettings.shippingMethods.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No additional shipping methods configured</p>
                        ) : (
                          <div className="space-y-3">
                            {shippingSettings.shippingMethods.map((method, index) => {
                              const pricing = method.pricing === 'by_area' ? 'by_area' : 'flat';
                              const rateFor = (areaId: string) => (method.areaRates || []).find(r => r.areaId === areaId);
                              return (
                                <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-3">
                                  {/* Name + remove */}
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="text"
                                      value={method.name}
                                      onChange={(e) => patchShippingMethod(index, { name: e.target.value })}
                                      placeholder="Shipping method name (e.g. Local Delivery)"
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    />
                                    <button
                                      onClick={() => removeShippingMethod(index)}
                                      className="text-red-600 hover:text-red-700 p-1"
                                      title="Remove method"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>

                                  {/* Pricing mode */}
                                  <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-200/70 p-1">
                                    <button
                                      type="button"
                                      onClick={() => patchShippingMethod(index, { pricing: 'flat' })}
                                      className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        pricing === 'flat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                      }`}
                                    >
                                      Flat price
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => patchShippingMethod(index, { pricing: 'by_area' })}
                                      className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        pricing === 'by_area' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                      }`}
                                    >
                                      Price by delivery area
                                    </button>
                                  </div>

                                  {pricing === 'flat' ? (
                                    <div className="w-32">
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                        <input
                                          type="number"
                                          value={method.price}
                                          onChange={(e) => patchShippingMethod(index, { price: parseFloat(e.target.value) || 0 })}
                                          placeholder="0.00"
                                          min="0"
                                          step="0.01"
                                          className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="space-y-1.5">
                                      <p className="text-xs text-gray-500">
                                        The customer sees the price once their address is entered. Areas left unchecked can't
                                        use this method.
                                        {serviceAreas.length === 0 && ' Create areas with "Manage Areas" below first.'}
                                      </p>
                                      {serviceAreas.map(area => {
                                        const rate = rateFor(area.id);
                                        return (
                                          <div key={area.id} className="flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5">
                                            <input
                                              type="checkbox"
                                              checked={!!rate}
                                              onChange={(e) => setMethodAreaRate(index, area.id, e.target.checked ? 0 : null)}
                                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                            />
                                            <span className="flex-1 text-sm text-gray-800 truncate">{area.name}</span>
                                            {rate && (
                                              <div className="relative w-24">
                                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                                <input
                                                  type="number"
                                                  value={rate.price}
                                                  onChange={(e) => setMethodAreaRate(index, area.id, parseFloat(e.target.value) || 0)}
                                                  min="0"
                                                  step="0.01"
                                                  className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Product surcharges */}
                                  <div className="pt-1 border-t border-gray-200">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="text-xs font-semibold text-gray-700">Product surcharges</span>
                                      <button
                                        type="button"
                                        onClick={() => addMethodSurcharge(index)}
                                        className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                                      >
                                        <Plus className="h-3.5 w-3.5" /> Add product rule
                                      </button>
                                    </div>
                                    {(method.productSurcharges || []).length === 0 ? (
                                      <p className="text-xs text-gray-400">
                                        Optional — extra fees per product for this method (e.g. carry-up charges).
                                      </p>
                                    ) : (
                                      <div className="space-y-2">
                                        {(method.productSurcharges || []).map((rule, ri) => {
                                          const q = Math.max(Number(rule.baseUnits) + 2, 1);
                                          const sample =
                                            (Number(rule.baseFee) || 0) +
                                            Math.max(0, q - (Number(rule.baseUnits) || 0)) * (Number(rule.extraPerUnit) || 0);
                                          return (
                                            <div key={ri} className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-2">
                                              <div className="flex items-center gap-2">
                                                <select
                                                  value={rule.stockItemId}
                                                  onChange={(e) => {
                                                    const p = availableProducts.find(ap => ap.id === e.target.value);
                                                    patchMethodSurcharge(index, ri, {
                                                      stockItemId: e.target.value,
                                                      productName: p?.name || '',
                                                    });
                                                  }}
                                                  className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                                >
                                                  <option value="">Choose product…</option>
                                                  {rule.stockItemId && !availableProducts.some(p => p.id === rule.stockItemId) && (
                                                    <option value={rule.stockItemId}>{rule.productName || 'Saved product'}</option>
                                                  )}
                                                  {availableProducts.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                  ))}
                                                </select>
                                                <button
                                                  type="button"
                                                  onClick={() => patchMethodSurcharge(index, ri, null)}
                                                  className="text-red-600 hover:text-red-700 p-1"
                                                  title="Remove rule"
                                                >
                                                  <X className="h-4 w-4" />
                                                </button>
                                              </div>
                                              <div className="grid grid-cols-3 gap-2">
                                                <div>
                                                  <label className="block text-[11px] text-gray-500 mb-0.5">First … units</label>
                                                  <input
                                                    type="number"
                                                    value={rule.baseUnits}
                                                    onChange={(e) => patchMethodSurcharge(index, ri, { baseUnits: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                                    min="0"
                                                    step="1"
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-[11px] text-gray-500 mb-0.5">Flat fee $</label>
                                                  <input
                                                    type="number"
                                                    value={rule.baseFee}
                                                    onChange={(e) => patchMethodSurcharge(index, ri, { baseFee: parseFloat(e.target.value) || 0 })}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-[11px] text-gray-500 mb-0.5">Each extra $</label>
                                                  <input
                                                    type="number"
                                                    value={rule.extraPerUnit}
                                                    onChange={(e) => patchMethodSurcharge(index, ri, { extraPerUnit: parseFloat(e.target.value) || 0 })}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                  />
                                                </div>
                                              </div>
                                              <p className="text-[11px] text-gray-500">
                                                {Number(rule.baseUnits) > 0
                                                  ? `First ${rule.baseUnits} unit${Number(rule.baseUnits) === 1 ? '' : 's'}: $${(Number(rule.baseFee) || 0).toFixed(2)} flat, then $${(Number(rule.extraPerUnit) || 0).toFixed(2)} per extra unit`
                                                  : `$${(Number(rule.extraPerUnit) || 0).toFixed(2)} per unit${Number(rule.baseFee) > 0 ? ` + $${Number(rule.baseFee).toFixed(2)} flat` : ''}`}
                                                {` — e.g. ${q} units = $${sample.toFixed(2)}`}
                                              </p>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Delivery Areas */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <h4 className="text-sm font-medium text-gray-900">Delivery Areas</h4>
                          </div>
                          <button
                            onClick={() => setIsAreasModalOpen(true)}
                            className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Manage Areas</span>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">
                          Restrict delivery orders to specific areas. Customers outside these areas can't check out with delivery.
                        </p>

                        <label className="flex items-center space-x-3 mb-3">
                          <input
                            type="checkbox"
                            checked={enforceDeliveryAreas}
                            onChange={(e) => setEnforceDeliveryAreas(e.target.checked)}
                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">Only deliver to selected areas</span>
                        </label>

                        {enforceDeliveryAreas && (
                          <div className="ml-7 space-y-2">
                            {serviceAreas.length === 0 ? (
                              <p className="text-sm text-gray-500 italic">
                                No areas yet — click "Manage Areas" to create your first delivery area.
                              </p>
                            ) : (
                              serviceAreas.map(area => (
                                <label key={area.id} className="flex items-start gap-3 p-2.5 bg-gray-50 rounded-lg cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={deliveryAreaIds.includes(area.id)}
                                    onChange={() => toggleDeliveryArea(area.id)}
                                    className="mt-0.5 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                  />
                                  <span className="min-w-0">
                                    <span className="block text-sm font-medium text-gray-800">{area.name}</span>
                                    <span className="block text-xs text-gray-500 truncate">
                                      {(area.postal_codes || []).length} postal code{(area.postal_codes || []).length === 1 ? '' : 's'}
                                      {(area.postal_codes || []).length > 0 && ` — ${(area.postal_codes || []).slice(0, 6).join(', ')}${(area.postal_codes || []).length > 6 ? '…' : ''}`}
                                    </span>
                                  </span>
                                </label>
                              ))
                            )}
                            {enforceDeliveryAreas && serviceAreas.length > 0 && deliveryAreaIds.filter(id => serviceAreas.some(a => a.id === id)).length === 0 && (
                              <p className="text-xs text-amber-600">
                                No areas selected — delivery checkout will be blocked everywhere until you select at least one area.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={saveShippingSettings}
                          disabled={isSavingShipping}
                          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingShipping ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Shipping Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Customer Wallet Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsWalletExpanded(!isWalletExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Wallet className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Customer Wallet</h3>
                          <p className="text-sm text-gray-600">Wallet visibility, top-ups, and bonus promotions</p>
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isWalletExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isWalletExpanded && (
                    <div className="px-4 pb-4 space-y-5">
                      {/* Enable wallet */}
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={walletEnabled}
                          onChange={(e) => {
                            setWalletEnabled(e.target.checked);
                            if (!e.target.checked) setWalletTopupEnabled(false);
                          }}
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 accent-indigo-600"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-800">Enable customer wallet</span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Shows the Wallet tab in the customer portal with the balance and activity. Balances are applied to orders and invoices.
                          </span>
                        </span>
                      </label>

                      {/* Enable top-ups */}
                      <label className={`flex items-start gap-3 ${walletEnabled ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                        <input
                          type="checkbox"
                          checked={walletTopupEnabled}
                          onChange={(e) => setWalletTopupEnabled(e.target.checked)}
                          disabled={!walletEnabled}
                          className="h-4 w-4 mt-0.5 rounded border-gray-300 accent-indigo-600 disabled:cursor-not-allowed"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-800">Let customers add money to their wallet</span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Adds an "Add money" button to the Wallet tab. Top-ups are always paid by card through Stripe — even for invoice-only customers.
                          </span>
                          {walletEnabled && walletTopupEnabled && !isStripeReady && (
                            <span className="block text-xs font-medium text-amber-600 mt-1">
                              Connect Stripe payments in your invoice settings — top-ups won't work until then.
                            </span>
                          )}
                        </span>
                      </label>

                      {/* Bonus tiers */}
                      {walletEnabled && walletTopupEnabled && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-900">Top-up Bonus Promotions</h4>
                            <button
                              onClick={() => setWalletPromos(prev => [...prev, { minAmount: '', bonus: '' }])}
                              className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                            >
                              <Plus className="h-4 w-4" /> Add tier
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">
                            Reward bigger top-ups — e.g. add $100, get a $5 bonus. The highest tier at or below the top-up amount applies.
                          </p>
                          {walletPromos.length === 0 ? (
                            <p className="text-sm text-gray-400">No bonus tiers yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {walletPromos.map((promo, index) => (
                                <div key={promo.id || `new-${index}`} className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      min="1"
                                      step="0.01"
                                      value={promo.minAmount}
                                      onChange={(e) =>
                                        setWalletPromos(prev => prev.map((p, i) => (i === index ? { ...p, minAmount: e.target.value } : p)))
                                      }
                                      placeholder="Top-up amount"
                                      className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                                    />
                                  </div>
                                  <span className="text-xs text-gray-400 shrink-0">gets</span>
                                  <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      min="0.01"
                                      step="0.01"
                                      value={promo.bonus}
                                      onChange={(e) =>
                                        setWalletPromos(prev => prev.map((p, i) => (i === index ? { ...p, bonus: e.target.value } : p)))
                                      }
                                      placeholder="Bonus"
                                      className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                                    />
                                  </div>
                                  <span className="text-xs text-gray-400 shrink-0">bonus</span>
                                  <button
                                    onClick={() => {
                                      if (promo.id) setRemovedPromoIds(prev => [...prev, promo.id!]);
                                      setWalletPromos(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                                    title="Remove tier"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Save Button */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={saveWalletSettings}
                          disabled={isSavingWallet}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingWallet ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Wallet Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Notifications Section */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="p-4">
                    <button
                      onClick={() => setIsNotifsExpanded(!isNotifsExpanded)}
                      className="w-full flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Mail className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Order Notifications</h3>
                          <p className="text-sm text-gray-600">Automated emails to customers and staff at each order stage</p>
                        </div>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isNotifsExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {isNotifsExpanded && (
                    <div className="px-4 pb-4 space-y-5">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">Customer emails</h4>
                        <p className="text-xs text-gray-500 mb-3">
                          Sent to the email entered at checkout. Pickup orders automatically use pickup wording ("ready for pickup" / "picked up").
                        </p>
                        <div className="space-y-3">
                          {([
                            {
                              key: 'order_placed' as const,
                              label: 'Order confirmation',
                              desc: 'Receipt with the order summary, sent the moment an order is placed.',
                            },
                            {
                              key: 'order_confirmed' as const,
                              label: 'Order confirmed',
                              desc: 'Sent when staff confirm the order and start preparing it.',
                            },
                            {
                              key: 'out_for_delivery' as const,
                              label: 'Out for delivery / Ready for pickup',
                              desc: 'Sent when the order goes out for delivery (or is ready for pickup).',
                            },
                            {
                              key: 'delivered' as const,
                              label: 'Delivered / Picked up',
                              desc: 'Sent when the order is marked delivered or picked up.',
                            },
                            {
                              key: 'cancelled' as const,
                              label: 'Order cancelled',
                              desc: 'Sent if an order is cancelled.',
                            },
                          ]).map(row => (
                            <label key={row.key} className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={orderNotifs[row.key]}
                                onChange={(e) => setOrderNotifs(prev => ({ ...prev, [row.key]: e.target.checked }))}
                                className="h-4 w-4 mt-0.5 rounded border-gray-300 accent-emerald-600"
                              />
                              <span>
                                <span className="block text-sm font-medium text-gray-800">{row.label}</span>
                                <span className="block text-xs text-gray-500 mt-0.5">{row.desc}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-medium text-gray-900 mb-1">Staff alerts</h4>
                        <label className="flex items-start gap-3 cursor-pointer mt-3">
                          <input
                            type="checkbox"
                            checked={orderNotifs.staff_new_order}
                            onChange={(e) => setOrderNotifs(prev => ({ ...prev, staff_new_order: e.target.checked }))}
                            className="h-4 w-4 mt-0.5 rounded border-gray-300 accent-emerald-600"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">New order alert</span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              Email your team every time a new online order comes in.
                            </span>
                          </span>
                        </label>
                        {orderNotifs.staff_new_order && (
                          <input
                            type="text"
                            value={staffAlertEmails}
                            onChange={(e) => setStaffAlertEmails(e.target.value)}
                            placeholder="orders@yourbusiness.com, manager@yourbusiness.com"
                            className="mt-3 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                          />
                        )}
                      </div>

                      {/* Save Button */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={saveNotificationSettings}
                          disabled={isSavingNotifs}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          {isSavingNotifs ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            'Save Notification Settings'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Management Section */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <ShoppingCart className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Order Management</h3>
                      <p className="text-sm text-gray-600">Online orders land in your Orders pipeline</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/stock')}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Manage Orders
                  </button>
                </div>

                {/* Embed on your website */}
                <div className="border border-gray-200 rounded-lg md:col-span-2">
                  <div className="p-4">
                    <button
                      onClick={() => {
                        setIsEmbedExpanded(!isEmbedExpanded);
                        if (!isEmbedExpanded && availableProducts.length === 0) loadAvailableProducts();
                      }}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Code2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-medium text-gray-900">Embed on Your Website</h3>
                          <p className="text-sm text-gray-600">Add your products, cart, and checkout to any site — Shopify-style</p>
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${isEmbedExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>

                  {isEmbedExpanded && (
                    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                      {/* Step 1: loader script */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">1. Add this once, before <code className="text-xs bg-gray-200 px-1 rounded">&lt;/body&gt;</code></h4>
                        <SnippetBox
                          code={`<script src="${window.location.origin}/embed.js" data-store="${selectedBusiness?.slug || 'your-business'}" async></script>`}
                        />
                      </div>

                      {/* Step 2: pick embed */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2">2. Place embeds anywhere on the page</h4>
                        <div className="flex gap-2 mb-2">
                          {([
                            { id: 'grid', label: 'Product grid' },
                            { id: 'product', label: 'Single product' },
                            { id: 'cart-button', label: 'Cart button' },
                            { id: 'account-button', label: 'Account button' },
                          ] as const).map(opt => (
                            <button
                              key={opt.id}
                              onClick={() => setEmbedType(opt.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                embedType === opt.id ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {embedType === 'product' && (
                          <select
                            value={embedProductId}
                            onChange={(e) => setEmbedProductId(e.target.value)}
                            className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">Choose a product…</option>
                            {availableProducts.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        )}

                        <SnippetBox
                          code={
                            embedType === 'grid'
                              ? `<div data-nexus-embed="grid"></div>`
                              : embedType === 'cart-button'
                              ? `<div data-nexus-embed="cart-button"></div>`
                              : embedType === 'account-button'
                              ? `<div data-nexus-embed="account-button"></div>`
                              : `<div data-nexus-embed="product" data-product-id="${embedProductId || 'PRODUCT_ID'}"></div>`
                          }
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          {embedType === 'account-button'
                            ? 'A profile icon for your site header. Customers sign in (or create an account) with an emailed code; a green dot shows when they\u2019re signed in, and the panel links to their orders, invoices, and wallet.'
                            : 'The cart and checkout open automatically as an overlay on your site — customers never leave your website. Only products with "Show in online store" enabled appear in embeds.'}
                        </p>
                      </div>

                      {/* Allowed origins */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-1">Allowed websites (optional)</h4>
                        <input
                          type="text"
                          value={embedOrigins}
                          onChange={(e) => setEmbedOrigins(e.target.value)}
                          placeholder="https://www.example.com, https://example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Comma-separated origins that may embed your store. Leave empty to allow any website.
                          Saved with your Payment Settings.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateTerritoriesModal
        isOpen={isAreasModalOpen}
        onClose={() => {
          setIsAreasModalOpen(false);
          loadServiceAreas();
        }}
        onSuccess={loadServiceAreas}
        businessId={selectedBusiness?.id}
      />
    </div>
  );
}
